import { Box, IconButton, Popper, Stack, alpha } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Instance } from '@popperjs/core';
import styles from '../FormRichText.styles';
import Quill from 'quill';
interface RichTextImageTooltipProps {
    quillEditorRef: any;
    richTextContainerRef: any;
    quillIcons: any;
    quill: any;
}
export default function RichTextImageTooltip(props: RichTextImageTooltipProps) {
    const { quillEditorRef, richTextContainerRef, quillIcons, quill } = props;
    const [activeState, setActiveState] = useState<boolean>(false);

    const positionImageTooltipRef = useRef<{ x: number; y: number }>({
        x: 0,
        y: 0,
    });
    const imageTooltipPopperRef = useRef<Instance>(null);
    const activeImageIndex = useRef<number>(0);
    const activeImageAlignment = useRef<string>('');

    const closeImageTooltipOnChangePos = () => {
        setActiveState(false);
        quillEditorRef.current.querySelectorAll('.image-hovered').forEach((el: any) => {
            el.classList.remove('image-hovered');
        });
    };

    const onImageHover = (event: any) => {
        closeImageTooltipOnChangePos();
        if (event.target.tagName.toLowerCase() === 'img') {
            const rect = event.target.getBoundingClientRect();
            positionImageTooltipRef.current = {
                x: rect.left + rect.width / 2 - 42, // 42px for the tooltip width
                y: rect.top,
            };
            if (imageTooltipPopperRef.current != null) {
                imageTooltipPopperRef.current.update();
            }
            setActiveState(true);
            event.target.classList.add('image-hovered');

            const imageBlot = Quill.find(event.target);
            activeImageIndex.current = quill.getIndex(imageBlot);
            activeImageAlignment.current = quill.getFormat(activeImageIndex.current)?.align ?? '';
        }
    };

    useEffect(() => {
        if (quillEditorRef.current) {
            quillEditorRef.current.addEventListener('mouseover', onImageHover);
        }

        let scrollContainer: any;
        if (richTextContainerRef.current) {
            scrollContainer = richTextContainerRef.current.closest('.scroll-content-container');
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', closeImageTooltipOnChangePos);
            }
        }

        window.addEventListener('scroll', closeImageTooltipOnChangePos);
        window.addEventListener('resize', closeImageTooltipOnChangePos);

        return () => {
            if (quillEditorRef.current) {
                quillEditorRef.current.removeEventListener('mouseover', onImageHover);
            }
            window.removeEventListener('scroll', closeImageTooltipOnChangePos);
            window.removeEventListener('resize', closeImageTooltipOnChangePos);
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', closeImageTooltipOnChangePos);
            }
        };
    }, []);

    const setImageAlign = (align: string) => {
        quill.setSelection(activeImageIndex.current, 0, 'silent');
        quill.format('align', align);
        closeImageTooltipOnChangePos();
    };

    return (
        <Popper
            placement={'top-start'}
            open={activeState}
            popperRef={imageTooltipPopperRef}
            anchorEl={{
                getBoundingClientRect: () => {
                    return new DOMRect(positionImageTooltipRef.current.x, positionImageTooltipRef.current.y, 0, 0);
                },
            }}
        >
            <Box sx={{ pb: 1 }}>
                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        borderRadius: 1,
                        boxShadow: (theme) => `0px 1px 4px 0px ${alpha(theme.palette.text.primary, 0.32)}`,
                        position: 'relative',
                    }}
                >
                    <Box
                        className={'arrow'}
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            ml: '-8px',
                            overflow: 'hidden',
                            width: '16px',
                            height: '10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:before': {
                                content: '""',
                                display: 'block',
                                width: '7px',
                                height: '7px',
                                backgroundColor: 'background.default',
                                boxShadow: (theme) => `0px 1px 4px 0px ${alpha(theme.palette.text.primary, 0.32)}`,
                                transform: 'rotate(45deg)',
                                mb: '10px',
                            },
                        }}
                    />
                    <Stack direction={'row'} alignItems={'center'} spacing={0.25} sx={{ p: 0.75 }}>
                        {['', 'center', 'right'].map((align) => {
                            return (
                                <IconButton
                                    key={align}
                                    sx={styles.imageAlignButton}
                                    className={activeImageAlignment.current == align ? 'active' : ''}
                                    onClick={() => {
                                        setImageAlign(align);
                                    }}
                                >
                                    <Box
                                        className={'image-align-button__svg'}
                                        dangerouslySetInnerHTML={{ __html: quillIcons.align[align] }}
                                    />
                                </IconButton>
                            );
                        })}
                    </Stack>
                </Box>
            </Box>
        </Popper>
    );
}
